import { useEffect, useRef, useState } from 'react'
import { AlertCircle, X } from 'lucide-react'
import {
  pdfBase64ToObjectUrl,
  timetableDataQualityApi,
  type TeacherRef,
} from '@/services/api/timetableDataQuality'
import { ApiErrorResponse } from '@/services/api/client'
import { extractErrorMessage } from '@/utils/errorHandler'

interface TokenPdfPanelProps {
  tokenName: string
  teachers: TeacherRef[]
  batchId: string | null
  onClose: () => void
}

export function TokenPdfPanel({ tokenName, teachers, batchId, onClose }: TokenPdfPanelProps) {
  const [selectedId, setSelectedId] = useState<string | null>(teachers[0]?.id ?? null)
  const [objectUrl, setObjectUrl] = useState<string | null>(null)
  const [page, setPage] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [noPdf, setNoPdf] = useState(false)
  const urlRef = useRef<string | null>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  useEffect(() => {
    let cancelled = false
    setObjectUrl(null)
    setPage(null)
    setError(null)
    setNoPdf(false)
    if (!selectedId) return
    setLoading(true)
    void (async () => {
      try {
        const context = await timetableDataQualityApi.getTeacherContext(
          selectedId,
          batchId ?? undefined,
          tokenName,
        )
        if (cancelled) return
        if (!context.pdf?.page) {
          setNoPdf(!context.pdf)
          setLoading(false)
          return
        }
        setPage(context.pdf.page)
        const result = await timetableDataQualityApi.getPdfPage(context.pdf.file_id, context.pdf.page)
        if (cancelled) return
        const url = pdfBase64ToObjectUrl(result.pdf_base64)
        urlRef.current = url
        setObjectUrl(url)
        setLoading(false)
      } catch (err) {
        if (cancelled) return
        if (err instanceof ApiErrorResponse && err.statusCode === 422) {
          setLoading(false)
          return
        }
        setError(extractErrorMessage(err, { loadError: 'PDF konnte nicht geladen werden' }))
        setLoading(false)
      }
    })()
    return () => {
      cancelled = true
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current)
        urlRef.current = null
      }
    }
  }, [selectedId, batchId, tokenName])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex h-[90vh] w-full max-w-[1100px] overflow-hidden rounded-lg bg-white shadow-xl">
        <div className="flex w-56 shrink-0 flex-col border-r border-gray-200">
          <div className="border-b border-gray-100 p-3">
            <p className="text-sm font-semibold text-gray-900">PDF · {tokenName}</p>
            <p className="text-xs text-gray-500">Personen-Seite, nicht Sammel-PDF</p>
          </div>
          <div className="min-h-0 flex-1 overflow-auto">
            {teachers.length === 0 ? (
              <p className="px-3 py-4 text-sm text-gray-500">Keine Lehrkräfte</p>
            ) : (
              teachers.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  aria-current={t.id === selectedId}
                  onClick={() => setSelectedId(t.id)}
                  className={`flex w-full items-center justify-between border-b border-gray-100 px-3 py-2 text-left text-sm ${
                    t.id === selectedId ? 'bg-blue-50' : 'hover:bg-gray-50'
                  }`}
                >
                  <span>
                    <span className="font-medium">{t.name}</span>
                    {t.code ? <span className="ml-1 font-mono text-[11px] text-gray-500">{t.code}</span> : null}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2">
            <p className="text-sm text-gray-500">{page ? `Seite ${page}` : 'PDF'}</p>
            <button type="button" onClick={onClose} className="rounded-lg p-2 hover:bg-gray-100">
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
          <div className="flex min-h-0 flex-1 items-center justify-center p-3">
            {loading ? (
              <p className="text-sm text-gray-500">PDF wird geladen…</p>
            ) : error ? (
              <p className="flex items-center text-sm text-red-600">
                <AlertCircle className="mr-2 h-4 w-4" />
                {error}
              </p>
            ) : noPdf || !selectedId ? (
              <p className="text-sm text-gray-500">Kein PDF-Stundenplan vorhanden.</p>
            ) : !objectUrl ? (
              <p className="text-sm text-gray-500">Seite unbekannt — das Sammel-PDF wird nicht geöffnet.</p>
            ) : (
              <iframe
                src={`${objectUrl}#zoom=page-width`}
                title="PDF-Stundenplan"
                className="h-full w-full rounded-lg border border-gray-200"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
